import { useEffect, useState } from "react";
import { Shield, Award, CheckCircle, Trophy } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";

export default function About() {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await api.getAllContent();
        setContent(data);
      } catch (err) {
        logger.warn("About content fetch failed:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const achievements = content.about_achievements 
    ? content.about_achievements.split('|')
    : [
        "Three-time WWF Tag Team Champions",
        "Longest combined reign as WWF Tag Team Champions",
        "Featured in WrestleMania IV, V, and VI",
        "Inducted into wrestling halls of fame",
        "One of the most recognizable tag teams"
      ];

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse text-demolition-red font-russo text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="bg-demolition-black min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 steel-mesh opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h1 
              className="font-russo text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tighter mb-4"
              data-testid="about-title"
            >
              ABOUT DEMOLITION
            </h1>
            <p className="font-teko text-xl sm:text-2xl text-demolition-chrome uppercase tracking-widest">
              The Most Dominant Tag Team in Wrestling History
            </p>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper" data-testid="bio-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] bg-demolition-subtle border border-white/10 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/6ydkdknv_Golden%20Demolition.png"
                  alt="Demolition Team"
                  className="w-full h-full object-cover img-grayscale hover:filter-none transition-all duration-500"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-demolition-red p-6">
                <Shield className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <div className="border-l-4 border-demolition-red pl-6">
                <h2 className="font-russo text-3xl text-white uppercase tracking-tighter mb-4">
                  THE LEGACY
                </h2>
              </div>
              <p className="font-mono text-white/70 leading-relaxed">
                {content.about_bio || "Demolition was one of the most dominant and feared tag teams in professional wrestling history. Formed in 1987, the team consisted of Ax (Bill Eadie) and Smash (Barry Darsow), later joined by Crush."}
              </p>
              <p className="font-mono text-white/70 leading-relaxed">
                Known for their intimidating face paint, spiked leather attire, and devastating finishing moves, 
                Demolition captured the hearts of wrestling fans worldwide. Their theme music alone was enough 
                to send shivers down the spines of their opponents. On April 17, 2026, Demolition was inducted 
                into the WWE Hall of Fame, Class of 2026, at Dolby Live at Park MGM in Las Vegas, NV.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-24 px-6 md:px-12" data-testid="achievements-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Award className="w-16 h-16 text-demolition-red mx-auto mb-4" />
            <h2 className="font-russo text-4xl text-white uppercase tracking-tighter">
              ACHIEVEMENTS & HIGHLIGHTS
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Special Gold Hall of Fame Box */}
            <div 
              className="bg-gradient-to-br from-yellow-500 to-yellow-600 border-2 border-yellow-400 p-6 flex items-start gap-4 shadow-lg shadow-yellow-500/20"
              data-testid="achievement-hof"
            >
              <Trophy className="w-6 h-6 text-black flex-shrink-0 mt-1" />
              <p className="font-mono text-black font-semibold">WWE Hall of Famers</p>
            </div>
            
            {achievements.map((achievement, index) => (
              <div 
                key={`achievement-${achievement.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                className="bg-demolition-paper border border-white/10 p-6 flex items-start gap-4 hover:border-demolition-red/50 transition-colors"
                data-testid={`achievement-${index}`}
              >
                <CheckCircle className="w-6 h-6 text-demolition-red flex-shrink-0 mt-1" />
                <p className="font-mono text-white/80">{achievement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ax & Smash Section */}
      <section className="py-24 px-6 md:px-12 bg-demolition-paper" data-testid="members-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-russo text-4xl text-white uppercase tracking-tighter text-center mb-16">
            THE TEAM
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Ax */}
            <div className="bg-demolition-subtle border border-white/10 overflow-hidden group">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/b2rea68b_demolition-ax.png"
                  alt="Ax"
                  className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 border-t border-white/10">
                <h3 className="font-russo text-3xl text-demolition-red uppercase tracking-tighter mb-2">
                  AX
                </h3>
                <p className="font-teko text-xl text-demolition-chrome uppercase tracking-wider mb-4">
                  Bill Eadie
                </p>
                <p className="font-mono text-white/60 text-sm">
                  The powerhouse of Demolition. Known for his incredible strength and 
                  devastating finishing moves. Ax brought raw power to every match.
                </p>
              </div>
            </div>

            {/* Smash */}
            <div className="bg-demolition-subtle border border-white/10 overflow-hidden group">
              <div className="aspect-square overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/jcoffkq5_demolition-smash-new.png"
                  alt="Smash"
                  className="w-full h-full object-cover img-grayscale group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6 border-t border-white/10">
                <h3 className="font-russo text-3xl text-demolition-red uppercase tracking-tighter mb-2">
                  SMASH
                </h3>
                <p className="font-teko text-xl text-demolition-chrome uppercase tracking-wider mb-4">
                  Barry Darsow
                </p>
                <p className="font-mono text-white/60 text-sm">
                  The technical genius of the team. Smash combined technical prowess 
                  with raw aggression to create an unstoppable force in the ring.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
